import { getSupabase } from '@/lib/supabase'

export interface BusinessHealthReview {
  review_id: number
  answers: Record<string, unknown>
  created_at: string
  created_by: string
  author_name: string | null
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  period_start: string
  period_end: string
}

export interface BusinessHealthWeek {
  week_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  is_available: boolean
}

export interface BusinessHealthCalendarWeek extends BusinessHealthWeek {
  period_id: number
}

export interface BusinessHealthPeriod {
  period_id: number
  period_start: string
  period_end: string
  month_number: number
  year_number: number
}

export interface BusinessHealthAccess {
  can_view: boolean
  can_edit: boolean
}

export interface BusinessHealthRecord {
  can_edit: boolean
  storage_scope: 'partner' | 'legacy_studio'
  partner_number: number
  partner_history: BusinessHealthReview[]
  current_period: BusinessHealthPeriod
  current_week_id: number
  weeks: BusinessHealthWeek[]
  current: BusinessHealthReview | null
  history: BusinessHealthReview[]
  periods: BusinessHealthPeriod[]
  calendar_weeks: BusinessHealthCalendarWeek[]
}

function isMissingRpc(error: unknown, name: string): boolean {
  if (!error || typeof error !== 'object') return false
  const candidate = error as { code?: string; message?: string }
  return candidate.code === 'PGRST202'
    || (candidate.code === '42883' && candidate.message?.includes(name) === true)
}

export async function getBusinessHealthAccess(studioId: number): Promise<BusinessHealthAccess> {
  const { data, error } = await (getSupabase() as any).rpc('get_studio_business_health_access', {
    p_studio_internal_id: studioId,
  })
  if (error) throw error
  return data as BusinessHealthAccess
}

export async function getBusinessHealth(studioId: number): Promise<BusinessHealthRecord> {
  const client = getSupabase() as any
  let { data, error } = await client.rpc('get_business_health_split', {
    p_studio_internal_id: studioId,
  })
  let storageScope: BusinessHealthRecord['storage_scope'] = 'partner'
  if (error && isMissingRpc(error, 'get_business_health_split')) {
    const legacy = await client.rpc('get_studio_business_health_weekly', {
      p_studio_internal_id: studioId,
    })
    data = legacy.data
    error = legacy.error
    storageScope = 'legacy_studio'
  }
  if (error) throw error
  const current = {
    ...data,
    storage_scope: storageScope,
    partner_history: storageScope === 'partner' ? data.partner_history : [],
  } as Omit<BusinessHealthRecord, 'periods' | 'calendar_weeks'>
  const { data: periods, error: periodsError } = await client
    .from('metric_periods')
    .select('period_id, period_start, period_end, month_number, year_number')
    .lte('period_start', current.current_period.period_start)
    .order('period_start', { ascending: false })
  if (periodsError) throw periodsError

  const loadedPeriods = (periods ?? []) as BusinessHealthPeriod[]
  const availablePeriods = loadedPeriods.some(period => period.period_id === current.current_period.period_id)
    ? loadedPeriods
    : [current.current_period, ...loadedPeriods]
  const periodIds = availablePeriods.map(period => period.period_id)
  const { data: weeks, error: weeksError } = await client
    .from('metric_weeks')
    .select('week_id, period_id, week_number_in_period, week_start, week_end')
    .in('period_id', periodIds)
    .order('week_start', { ascending: true })
  if (weeksError) throw weeksError

  const currentWeekAvailability = new Map(current.weeks.map(week => [week.week_id, week.is_available]))
  const loadedWeeks = (weeks ?? []) as Array<Omit<BusinessHealthCalendarWeek, 'is_available'>>
  const loadedWeekIds = new Set(loadedWeeks.map(week => week.week_id))
  return {
    ...current,
    periods: availablePeriods,
    calendar_weeks: [
      ...loadedWeeks.map(week => ({
        ...week,
        is_available: week.period_id !== current.current_period.period_id || currentWeekAvailability.get(week.week_id) === true,
      })),
      ...current.weeks.filter(week => !loadedWeekIds.has(week.week_id)).map(week => ({
        ...week,
        period_id: current.current_period.period_id,
      })),
    ],
  }
}

export async function saveBusinessHealth(
  studioId: number,
  weekId: number,
  answers: Record<string, string | null>,
  storageScope: BusinessHealthRecord['storage_scope'],
): Promise<{ studio_saved: boolean; partner_saved: boolean }> {
  if (storageScope === 'legacy_studio') {
    const { error } = await (getSupabase() as any).rpc('save_studio_business_health_weekly', {
      p_studio_internal_id: studioId,
      p_week_id: weekId,
      p_answers: answers,
    })
    if (error) throw error
    return { studio_saved: true, partner_saved: false }
  }
  const { data, error } = await (getSupabase() as any).rpc('save_business_health_split', {
    p_studio_internal_id: studioId,
    p_week_id: weekId,
    p_answers: answers,
  })
  if (error) throw error
  return data as { studio_saved: boolean; partner_saved: boolean }
}

export interface BusinessHealthQuarterStudio {
  studio_internal_id: number
  week_start: string | null
  answers: Record<string, unknown> | null
}

export interface BusinessHealthQuarter {
  storage_scope: 'partner' | 'legacy_studio'
  studios: BusinessHealthQuarterStudio[]
  entrepreneur: { week_start: string; answers: Record<string, unknown> } | null
}

export async function getPartnerBusinessHealthQuarter(partnerNumber: number, year: number, quarter: number): Promise<BusinessHealthQuarter> {
  const client = getSupabase() as any
  const { data, error } = await client.rpc('get_partner_business_health_quarter', {
    p_partner_number: partnerNumber,
    p_year: year,
    p_quarter: quarter,
  })
  if (!error) return { ...(data as Omit<BusinessHealthQuarter, 'storage_scope'>), storage_scope: 'partner' }
  if (!isMissingRpc(error, 'get_partner_business_health_quarter')) throw error

  // Preview before the migration: canonical studio list plus the existing gated
  // read RPC. The shared entrepreneur block has no source yet and stays empty.
  const { data: available, error: studiosError } = await client.rpc('get_my_studios')
  if (studiosError) throw studiosError
  const candidates = ((available ?? []) as Array<{ studio_internal_id: number; partner_number: number | null; access_kind: string }>)
    .filter(studio => studio.partner_number === partnerNumber
      && (studio.access_kind === 'owner' || studio.access_kind === 'management_company'))
  if (candidates.length === 0) throw new Error('Нет доступа к здоровью бизнеса этого франчайзи.')

  const results = await Promise.allSettled(candidates.map(studio => client.rpc('get_studio_business_health_weekly', {
    p_studio_internal_id: studio.studio_internal_id,
  })))
  const studios: BusinessHealthQuarterStudio[] = []
  for (let index = 0; index < results.length; index += 1) {
    const result = results[index]
    if (result.status === 'rejected') throw result.reason
    if (result.value.error) {
      if (result.value.error.code === '42501') continue
      throw result.value.error
    }
    const history = (result.value.data?.history ?? []) as BusinessHealthReview[]
    const latest = history
      .filter(review => Number(review.week_start.slice(0, 4)) === year
        && Math.ceil(Number(review.week_start.slice(5, 7)) / 3) === quarter)
      .sort((a, b) => b.week_start.localeCompare(a.week_start)
        || b.created_at.localeCompare(a.created_at) || b.review_id - a.review_id)[0]
    studios.push({
      studio_internal_id: candidates[index].studio_internal_id,
      week_start: latest?.week_start ?? null,
      answers: latest?.answers ?? null,
    })
  }
  if (studios.length === 0) throw new Error('Нет доступа к здоровью бизнеса этого франчайзи.')
  return { storage_scope: 'legacy_studio', studios, entrepreneur: null }
}
